import { NavLink, Outlet, Navigate } from "react-router-dom";
import {
  ArrowPathIcon,
  ClipboardDocumentListIcon,
  GiftTopIcon,
  HomeIcon,
  KeyIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  UserPlusIcon,
  ArrowRightStartOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { useAuthStore } from "../stores/auth";
import { isInternalEmail } from "./admin/isAdmin";

const adminLinks = [
  { to: "/admin", end: true, icon: HomeIcon, label: "Overview" },
  { to: "/admin/users", end: false, icon: UserGroupIcon, label: "Users" },
  { to: "/admin/transfers", end: false, icon: ArrowPathIcon, label: "Transfers" },
  { to: "/admin/activations", end: false, icon: ShieldCheckIcon, label: "Activations" },
  { to: "/admin/licenses", end: false, icon: KeyIcon, label: "Licences" },
  { to: "/admin/gift", end: false, icon: GiftTopIcon, label: "Gift Key" },
  { to: "/admin/allowlist", end: false, icon: UserPlusIcon, label: "Allowlist" },
  { to: "/admin/waitlist", end: false, icon: ClipboardDocumentListIcon, label: "Waitlist" },
];

export function AdminLayout() {
  const { session, user, initialized, signOut } = useAuthStore();

  if (!initialized) {
    return (
      <div className="workspace-shell flex min-h-[calc(100vh-5rem)] items-center justify-center bg-cream-100/50">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-forest-300 border-t-forest-700" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/portal/login" replace />;
  }

  if (!isInternalEmail(user?.email)) {
    return (
      <div className="workspace-shell flex min-h-[calc(100vh-5rem)] items-center justify-center bg-cream-100/50">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl font-bold text-forest-900">Not Authorized</h1>
          <p className="mt-2 text-forest-600">
            Admin access requires a @roguelite.tech email address.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-shell flex min-h-[calc(100vh-5rem)]">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 flex-shrink-0 border-r border-forest-800/10 bg-forest-950 lg:block">
        <div className="flex h-full flex-col">
          <div className="border-b border-cream-200/10 p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-gold-200">
              Admin Panel
            </p>
            <p className="mt-1 truncate font-display text-sm font-medium text-cream-100">
              {user?.email}
            </p>
          </div>

          <nav className="flex-1 overflow-y-auto p-3">
            <ul className="space-y-1">
              {adminLinks.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end={link.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-cream-100 text-forest-900"
                          : "text-cream-200/90 hover:bg-forest-900 hover:text-cream-200"
                      }`
                    }
                  >
                    <link.icon className="h-5 w-5" />
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="border-t border-cream-200/10 p-3">
            <NavLink
              to="/portal/dashboard"
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-cream-200/80 transition-colors hover:bg-forest-900 hover:text-cream-200"
            >
              <HomeIcon className="h-5 w-5" />
              Back to portal
            </NavLink>
            <button
              onClick={signOut}
              className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-cream-200/80 transition-colors hover:bg-forest-900 hover:text-cream-200"
            >
              <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="workspace-content min-w-0 flex-1 bg-cream-100/50 p-6 pb-20 md:p-8 lg:pb-8">
        <Outlet />
      </main>

      {/* Mobile bottom nav — condensed to the most-used admin destinations */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-forest-800/10 bg-forest-950 lg:hidden">
        <div className="flex items-center justify-around">
          {[
            adminLinks[0]!, // Overview
            adminLinks[1]!, // Users
            adminLinks[2]!, // Transfers
            adminLinks[6]!, // Allowlist
            adminLinks[7]!, // Waitlist
          ].map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-2 py-2.5 text-[10px] font-medium transition-colors ${
                  isActive ? "text-cream-100" : "text-cream-200/80"
                }`
              }
            >
              <link.icon className="h-5 w-5" />
              {link.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
